function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import * as PropTypes from 'prop-types';
import SpzaComponent from '@shared/components/spzaComponent';
import freeTrialBadge from '@shared/images/freeTrial.svg';
import pdpPrivateBadge from '@shared/images/pdp-privateBadge.svg';
import { TermsStrings, CatalogOfferType } from '@shared/Models';
import { Constants } from '@shared/utils/constants';
import { getWindow } from '@shared/services/window';
import { SpzaInstrumentService } from '@shared/services/telemetry/spza/spzaInstrument';
import { Text, Stack, DetailsList, Spinner, SpinnerSize, Selection, SelectionMode, mergeStyleSets } from '@fluentui/react';
import * as DetailUtils from '@shared/utils/detailUtils';
import { getVariablePriceStringWithDefault, getBillingTermTranslated, addFuturePricing } from '@shared/utils/pricing';
import { updateCart, getBillingPlanPricesPerTermDuration, convertTermToString } from '@shared/utils/checkoutUtils';
import FuturePriceWarning from '@shared/components/futurePriceWarning';
import { withRouter } from '@shared/routerHistory';
import { NeutralColors } from '@fluentui/theme';
import { logger } from '@src/logger';
import classNames from 'classnames';
import { TelemetryImage } from '@shared/components/telemetryImage';
import { isOneTimePaymentOffer } from '@shared/utils/onetimepaymentoffers';
import { MSuserErrorBanner } from '@appsource/components';
export const plansListStyles = mergeStyleSets({
    container: {
        marginTop: '4px',
        color: '#323130'
    },
    bulletColor: {
        color: '#323130'
    },
    pricingHeader: {
        color: NeutralColors.gray130,
        marginBottom: '8px'
    },
    pricingText: {
        marginBottom: '8px'
    },
    pricingFuturePriceText: {
        marginBottom: '4px'
    },
    privateImg: {
        height: '20px'
    },
    offerImg: {
        display: 'inline-block'
    }
});
export const PricingTable = ({ plan, futurePriceMonthly, futurePriceYearly, termDurations, isQuantifiable, hasFreeTrial, billingCountryCode, context, getTermByTermId, isOneTimePaymentOffer, locale })=>{
    // All in all - creates different margin if there's any future price
    const priceTextClass = classNames({
        [plansListStyles.pricingText]: !futurePriceMonthly && !futurePriceYearly,
        [plansListStyles.pricingFuturePriceText]: futurePriceMonthly || futurePriceYearly
    });
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: /*#__PURE__*/ _jsx("div", {
            className: `pricesTableBorder ${plansListStyles.container}`,
            children: /*#__PURE__*/ _jsx(Stack, {
                tokens: {
                    childrenGap: 10
                },
                children: termDurations.map((termDuration)=>{
                    const termPrices = getBillingPlanPricesPerTermDuration({
                        plan,
                        billingDuration: termDuration
                    });
                    return /*#__PURE__*/ _jsxs(Stack, {
                        children: [
                            !isOneTimePaymentOffer && /*#__PURE__*/ _jsx(Stack.Item, {
                                className: plansListStyles.pricingHeader,
                                children: /*#__PURE__*/ _jsx(Text, {
                                    variant: "smallPlus",
                                    children: `${getBillingTermTranslated({
                                        context,
                                        term: termDuration,
                                        isSubscription: true
                                    })} `
                                })
                            }),
                            /*#__PURE__*/ _jsx(Stack.Item, {
                                className: "ms-fontSize-14",
                                children: /*#__PURE__*/ _jsx("ul", {
                                    className: "plan-prices-list",
                                    children: termPrices.map((termPriceObject)=>{
                                        const term = getTermByTermId({
                                            planId: plan.planId,
                                            termId: termPriceObject.termId
                                        });
                                        return /*#__PURE__*/ _jsxs("li", {
                                            className: "price-bullet-first",
                                            children: [
                                                /*#__PURE__*/ _jsx(Text, {
                                                    className: priceTextClass,
                                                    variant: "medium",
                                                    children: convertTermToString({
                                                        context,
                                                        term,
                                                        countryCode: billingCountryCode,
                                                        isQuantifiable,
                                                        hasFreeTrial,
                                                        locale
                                                    })
                                                }),
                                                addFuturePricing(termDuration, futurePriceMonthly, futurePriceYearly)
                                            ]
                                        }, `${termPriceObject.termId}`);
                                    })
                                })
                            })
                        ]
                    }, `${plan.planId}-${termDuration}`);
                })
            })
        })
    });
};
PricingTable.contextTypes = {
    loc: PropTypes.func,
    locParams: PropTypes.func
};
class PlansListComponent extends SpzaComponent {
    filterAndSortPlans({ plans = [] }) {
        return plans.filter((plan)=>!!plan.availabilities.length).sort((a, b)=>{
            var _a_displayName, _b_displayName;
            return Number(a.displayRank) - Number(b.displayRank) || ((_b_displayName = b.displayName) === null || _b_displayName === void 0 ? void 0 : _b_displayName.toUpperCase().localeCompare((_a_displayName = a.displayName) === null || _a_displayName === void 0 ? void 0 : _a_displayName.toUpperCase()));
        });
    }
    logClickEvent(clickAction, details) {
        var _getWindow_location, _getWindow;
        const payload = {
            page: (_getWindow = getWindow()) === null || _getWindow === void 0 ? void 0 : (_getWindow_location = _getWindow.location) === null || _getWindow_location === void 0 ? void 0 : _getWindow_location.href,
            action: Constants.Telemetry.Action.Click,
            actionModifier: clickAction,
            appName: this.props.app.entityId,
            details: JSON.stringify(DetailUtils.addSaaSBundleProperties(details, this.props.app))
        };
        SpzaInstrumentService.getProvider().probe('logTelemetryInfo', payload);
        logger.info(payload.details, {
            action: payload.action,
            actionModifier: payload.actionModifier,
            appName: payload.appName
        });
    }
    renderImpl() {
        var _items__product_plans, _items__product, _items_, _items__product_plans1, _items__product1, _items_1, _items_2;
        const { app, wizardLoading: isCustomerLoading, checkout: { items }, location, users: { tenantsDetails: { details: { tenantId } } } } = this.props;
        const hasNoPlansOrAvailabilities = !((_items_ = items[0]) === null || _items_ === void 0 ? void 0 : (_items__product = _items_.product) === null || _items__product === void 0 ? void 0 : (_items__product_plans = _items__product.plans) === null || _items__product_plans === void 0 ? void 0 : _items__product_plans.length) || !(items === null || items === void 0 ? void 0 : (_items_1 = items[0]) === null || _items_1 === void 0 ? void 0 : (_items__product1 = _items_1.product) === null || _items__product1 === void 0 ? void 0 : (_items__product_plans1 = _items__product1.plans) === null || _items__product_plans1 === void 0 ? void 0 : _items__product_plans1.some((plan)=>plan.availabilities.length));
        let sortedItemSkus = [];
        if (!hasNoPlansOrAvailabilities) {
            const currentItem = items[0];
            const { plans } = currentItem.product;
            sortedItemSkus = this.filterAndSortPlans({
                plans
            });
            if (!this.selection.getSelectedCount()) {
                const planIdFromUrlParams = location.query[Constants.Checkout.DefaultPlanIdQueryParam];
                const planId = planIdFromUrlParams || sortedItemSkus[0].planId;
                // Update UI
                this.selection.setKeySelected(planId, true, false);
                // Update cart
                this.onPlanSelectionItemInvoked(currentItem.product.getPlanByPlanId(planId));
            }
        }
        return /*#__PURE__*/ _jsxs("div", {
            children: [
                /*#__PURE__*/ _jsxs(Stack, {
                    children: [
                        /*#__PURE__*/ _jsx(Text, {
                            className: "new-checkout-step-header ms-fontWeight-semibold",
                            children: this.context.loc('Checkout_SelectPlanComponentTitle', 'Select a plan')
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "mb-32 mt-20",
                            children: [
                                (items === null || items === void 0 ? void 0 : (_items_2 = items[0]) === null || _items_2 === void 0 ? void 0 : _items_2.appImageUri) && /*#__PURE__*/ _jsx(TelemetryImage, {
                                    src: items[0].appImageUri,
                                    className: plansListStyles.offerImg,
                                    imgClassName: "img-icon",
                                    alt: "offer icon"
                                }),
                                " ",
                                app && app.title && /*#__PURE__*/ _jsx(Text, {
                                    className: "plan-panel-title-header",
                                    children: app.title
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(MSuserErrorBanner, {
                    context: this.context,
                    tenantId: tenantId
                }),
                isCustomerLoading ? /*#__PURE__*/ _jsx(Spinner, {
                    size: SpinnerSize.small
                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        app.futurePricesInformation && /*#__PURE__*/ _jsx(FuturePriceWarning, {
                            futurePrices: app.futurePricesInformation
                        }, "futurePriceWarning"),
                        hasNoPlansOrAvailabilities ? /*#__PURE__*/ _jsx("div", {
                            className: "loadingContainer",
                            children: /*#__PURE__*/ _jsx("div", {
                                className: "loading",
                                children: /*#__PURE__*/ _jsx(Spinner, {
                                    label: this.context.loc('Gallery_Loading', 'Loading...'),
                                    ariaLive: "assertive",
                                    labelPosition: "left"
                                })
                            })
                        }) : /*#__PURE__*/ _jsx(DetailsList, {
                            "data-bi-id": 'plansList',
                            "data-bi-name": 'Plan Selection',
                            className: app.futurePricesInformation ? 'minPlansListWidth' : 'minPlansListWidth mt-20',
                            items: sortedItemSkus,
                            columns: this.columns,
                            setKey: "set",
                            getKey: (item)=>item.id,
                            onShouldVirtualize: ()=>false,
                            onActiveItemChanged: this.onPlanSelectionItemInvoked,
                            selection: this.selection,
                            selectionMode: SelectionMode.single,
                            isHeaderVisible: false,
                            selectionPreservedOnEmptyClick: true,
                            onRowDidMount: this.focusIntoSelectedPlan,
                            componentRef: this.saveComponentRef,
                            checkButtonAriaLabel: "select plan"
                        })
                    ]
                })
            ]
        });
    }
    constructor(props, context){
        super(props, context), _define_property(this, "context", void 0), _define_property(this, "selection", void 0), _define_property(this, "columns", void 0), _define_property(this, "detailsList", void 0), _define_property(this, "MAX_QUANTITY", 1000000), _define_property(this, "onPlanSelectionItemInvoked", (plan)=>{
            var _this_props_checkout_items;
            if (!plan) {
                const selection = this.selection.getSelection();
                if (selection && selection.length > 0) {
                    plan = selection[0];
                }
                return;
            }
            const { id, product } = (_this_props_checkout_items = this.props.checkout.items) === null || _this_props_checkout_items === void 0 ? void 0 : _this_props_checkout_items[0];
            this.logClickEvent('Plan Panel Selection', {
                oldPlan: id,
                newPlan: plan.id
            });
            // If current seat count is not within min/max range of new plan, then update to closest allowed value within new plan
            let currentSeatCount = plan.isQuantifiable && plan.minQuantity || 1;
            if (plan.isQuantifiable) {
                if (plan.minQuantity && currentSeatCount < plan.minQuantity) {
                    currentSeatCount = plan.minQuantity;
                } else if (plan.maxQuantity && currentSeatCount > plan.maxQuantity) {
                    currentSeatCount = plan.maxQuantity;
                }
            }
            // Keep the term selection between plan changes. So if 'yearly' was selected with the current plan, then keep 'yearly' selected for the new plan as well
            // If new plan does not have the currently selected term, then just default to first one
            const firstTerm = product.getFirstTermFromPlan(plan.planId);
            const price = firstTerm.price.msrp;
            const billingTerm = firstTerm.termUnits;
            const selectedTermId = plan.hasFreeTrials ? firstTerm.freeTrialTermId : firstTerm.termId;
            const isSaaS = product.offerType === CatalogOfferType.SaaS;
            const isPrivateAndSaaS = plan.isPrivate && isSaaS;
            const cartProps = {
                props: this.props,
                id: plan.planId,
                title: plan.displayName,
                description: plan.description,
                quantity: currentSeatCount.toString(),
                isQuantifiable: plan.isQuantifiable,
                minQuantity: plan.minQuantity,
                maxQuantity: plan.maxQuantity,
                billingTerm,
                termId: selectedTermId,
                isRecurring: !isPrivateAndSaaS,
                showAutoRenewMessage: isPrivateAndSaaS,
                skuId: plan.skuId,
                bigId: product.bigId,
                hasFreeTrial: plan.hasFreeTrials,
                price
            };
            updateCart(cartProps);
        }), _define_property(this, "onRenderPanelTitleView", (plan)=>{
            var _futurePrices_skus, _this_props_app, _this_props_app_licenseManagement, _this_props_app1, _this_props_checkout_items__unitPrice, _this_props_checkout_items_, _this_selection_getSelection_;
            // If any of the termPrices have a free trial then render the free trial badge next to the plan title
            const { hasFreeTrials, isPrivate } = plan;
            const { minQuantity, maxQuantity, isQuantifiable, description, displayName } = plan;
            const { config, checkout, app: { entityId }, billingCountry, isProfessionalService } = this.props;
            const futurePrices = this.props.app.futurePricesInformation;
            const futureSku = futurePrices === null || futurePrices === void 0 ? void 0 : (_futurePrices_skus = futurePrices.skus) === null || _futurePrices_skus === void 0 ? void 0 : _futurePrices_skus.find((futurePriceSku)=>futurePriceSku.id === plan.planId);
            const { futurePriceMonthly, futurePriceYearly } = getVariablePriceStringWithDefault({
                sku: {
                    id: plan.planId,
                    description: plan.description,
                    title: plan.displayName,
                    startingPrice: {
                        unit: TermsStrings.Term1MString,
                        value: 0
                    }
                },
                showPriceColumn: true,
                showYearlyPrice: true,
                showMonthlyPrice: true,
                isMicrosoftManaged: ((_this_props_app = this.props.app) === null || _this_props_app === void 0 ? void 0 : _this_props_app.licenseManagement) && ((_this_props_app1 = this.props.app) === null || _this_props_app1 === void 0 ? void 0 : (_this_props_app_licenseManagement = _this_props_app1.licenseManagement) === null || _this_props_app_licenseManagement === void 0 ? void 0 : _this_props_app_licenseManagement.isMicrosoftManaged),
                countryCode: this.props.billingCountry,
                currency: (_this_props_checkout_items_ = this.props.checkout.items[0]) === null || _this_props_checkout_items_ === void 0 ? void 0 : (_this_props_checkout_items__unitPrice = _this_props_checkout_items_.unitPrice) === null || _this_props_checkout_items__unitPrice === void 0 ? void 0 : _this_props_checkout_items__unitPrice.currency,
                context: this.context,
                futureSku,
                locale: this.props.config.locale
            });
            // Generate texts per term, clone so I don't mess up the redux store
            const planTermDurations = [
                ...this.props.checkout.items[0].product.getTermDurationsByPlanId(plan.planId)
            ].sort();
            const maximalQuantity = maxQuantity > this.MAX_QUANTITY ? this.MAX_QUANTITY : maxQuantity;
            const isPlanSelected = ((_this_selection_getSelection_ = this.selection.getSelection()[0]) === null || _this_selection_getSelection_ === void 0 ? void 0 : _this_selection_getSelection_.id) === plan.id;
            const planTitleClass = classNames({
                defaultPlanTitle: true,
                'free-trial-text': hasFreeTrials,
                'plan-selected': isPlanSelected
            });
            const freeTrialClass = classNames({
                'mt-12': hasFreeTrials,
                'mt-20': !hasFreeTrials
            });
            const pricingTitleClass = classNames({
                'mute-pricing-header ms-fontWeight-semibold': isPlanSelected,
                'mute-pricing-header': !isPlanSelected
            });
            return /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsxs(Stack, {
                        horizontal: true,
                        wrap: true,
                        tokens: {
                            childrenGap: 5
                        },
                        children: [
                            /*#__PURE__*/ _jsx(Text, {
                                variant: "medium",
                                className: planTitleClass,
                                children: displayName
                            }),
                            hasFreeTrials && /*#__PURE__*/ _jsx(TelemetryImage, {
                                src: freeTrialBadge,
                                alt: "free trial tag"
                            }),
                            isPrivate && /*#__PURE__*/ _jsx(TelemetryImage, {
                                src: pdpPrivateBadge,
                                imgClassName: plansListStyles.privateImg,
                                alt: "private plan"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: freeTrialClass,
                        children: /*#__PURE__*/ _jsxs(Stack, {
                            horizontal: true,
                            wrap: true,
                            tokens: {
                                childrenGap: 0
                            },
                            children: [
                                /*#__PURE__*/ _jsx(Stack, {
                                    children: /*#__PURE__*/ _jsxs("div", {
                                        className: "descriptionContainer",
                                        children: [
                                            /*#__PURE__*/ _jsx(Text, {
                                                className: pricingTitleClass,
                                                children: this.context.loc('Description', 'Description')
                                            }),
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "ms-fontSize-14 defaultPlanTitle mt-4",
                                                // eslint-disable-next-line
                                                dangerouslySetInnerHTML: {
                                                    __html: description
                                                }
                                            }),
                                            isQuantifiable && /*#__PURE__*/ _jsxs("div", {
                                                className: "mt-12",
                                                children: [
                                                    /*#__PURE__*/ _jsx(Text, {
                                                        className: pricingTitleClass,
                                                        children: this.context.loc('Checkout_BillingOptions_NumberOfUsers', 'Number of users')
                                                    }),
                                                    /*#__PURE__*/ _jsx("br", {}),
                                                    /*#__PURE__*/ _jsxs(Text, {
                                                        className: "ms-fontSize-14 defaultPlanTitle",
                                                        children: [
                                                            minQuantity.toLocaleString(config.locale),
                                                            "-",
                                                            maximalQuantity.toLocaleString(config.locale)
                                                        ]
                                                    })
                                                ]
                                            })
                                        ]
                                    })
                                }),
                                !isProfessionalService && /*#__PURE__*/ _jsx(Stack, {
                                    children: /*#__PURE__*/ _jsxs("div", {
                                        className: "prices-container",
                                        children: [
                                            /*#__PURE__*/ _jsx(Text, {
                                                className: pricingTitleClass,
                                                children: this.context.loc('Checkout_PriceAndPaymentOptions', 'Price/payment options')
                                            }),
                                            /*#__PURE__*/ _jsx(PricingTable, {
                                                context: this.context,
                                                termDurations: planTermDurations,
                                                plan: plan,
                                                billingCountryCode: billingCountry,
                                                futurePriceMonthly: futurePriceMonthly,
                                                futurePriceYearly: futurePriceYearly,
                                                hasFreeTrial: hasFreeTrials,
                                                isQuantifiable: isQuantifiable,
                                                getTermByTermId: checkout.items[0].product.getTermByTermId,
                                                isOneTimePaymentOffer: isOneTimePaymentOffer(entityId),
                                                locale: this.props.config.locale
                                            })
                                        ]
                                    })
                                })
                            ]
                        })
                    })
                ]
            });
        }), _define_property(this, "saveComponentRef", (detailsList)=>this.detailsList = detailsList), _define_property(this, "focusIntoSelectedPlan", ()=>{
            const selectedIndecies = this.selection.getSelectedIndices();
            if (selectedIndecies === null || selectedIndecies === void 0 ? void 0 : selectedIndecies.length) {
                var _this_detailsList;
                (_this_detailsList = this.detailsList) === null || _this_detailsList === void 0 ? void 0 : _this_detailsList.focusIndex(selectedIndecies[0]);
            }
        });
        this.context = context;
        this.columns = [
            {
                key: 'title',
                name: this.context.loc('Checkout_SelectPlanComponentTitle', 'Select a plan'),
                fieldName: 'title',
                minWidth: 100,
                maxWidth: 150,
                isResizable: true,
                isMultiline: true,
                onRender: (item)=>this.onRenderPanelTitleView(item)
            }
        ];
        this.selection = new Selection({
            getKey: (data)=>data.planId,
            selectionMode: SelectionMode.single
        });
    }
}
export const PlansList = withRouter(PlansListComponent);
PlansListComponent.contextTypes = {
    loc: PropTypes.func,
    locParams: PropTypes.func
};
